/**
 * RenewalPopup - Shows once per day for corporate users when subscription is in renewal period.
 * Only visible to user type 'corporate' (main account holder).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { getActiveSubscriptions } from '../../utils/subscriptionCache';
import { getModuleTabLabel, normalizeModuleTabKey } from '../../utils/subscriptionModuleTabs';
import './RenewalPopup.css';

/** Set to `false` when you are done styling / testing — restores corporate + renewal + once-per-day logic. */
const FORCE_SHOW_RENEWAL_POPUP = false;

const RENEWAL_POPUP_STORAGE_KEY = 'renewal_popup_shown_date';

function getTodayDateStr() {
  return new Date().toISOString().split('T')[0];
}

function wasPopupShownToday() {
  try {
    const stored = localStorage.getItem(RENEWAL_POPUP_STORAGE_KEY);
    return stored === getTodayDateStr();
  } catch { return false; }
}

function markPopupShownToday() {
  try {
    localStorage.setItem(RENEWAL_POPUP_STORAGE_KEY, getTodayDateStr());
  } catch (e) {}
}

function isCorporateUser() {
  try {
    const user = localStorage.getItem('user');
    if (!user) return false;
    const parsed = JSON.parse(user);
    const type = (parsed?.type || parsed?.role || parsed?.userType || '').toLowerCase();
    return type === 'corporate';
  } catch { return false; }
}

function isWithinRenewalPeriod(subscription) {
  if (!subscription?.end_date) return false;
  const endDate = new Date(subscription.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  const subType = (subscription.subscription_type || '').toLowerCase();
  const renewalDays = subType === 'yearly' ? 10 : 5;
  return daysUntilExpiry >= 0 && daysUntilExpiry <= renewalDays;
}

export function getRenewalSubscriptionLabels(subscription) {
  const plan = subscription?.SubscriptionPlan;
  const planName = (plan?.display_name || plan?.name || subscription?.plan_name || '').trim();
  const moduleRaw = (
    plan?.Module?.display_name ||
    plan?.Module?.name ||
    subscription?.module_name ||
    ''
  ).trim();
  const moduleName = getModuleTabLabel(normalizeModuleTabKey(moduleRaw), moduleRaw);
  return { moduleName, planName };
}

export function RenewalAlertMessage({ subscription }) {
  const { moduleName, planName } = getRenewalSubscriptionLabels(subscription);
  if (!moduleName && !planName) {
    return 'Your subscription is coming up for renewal. Please renew now to have uninterrupted usage.';
  }
  return (
    <>
      Your{' '}
      {moduleName ? <strong className="renewal-alert-name">{moduleName}</strong> : null}
      {moduleName && planName ? ' — ' : null}
      {planName ? <strong className="renewal-alert-name">{planName}</strong> : null}
      {' '}subscription is coming up for renewal. Please renew now to have uninterrupted usage.
    </>
  );
}

function isExpired(subscription) {
  if (!subscription?.end_date) return false;
  const endDate = new Date(subscription.end_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  return endDate < today;
}

export default function RenewalPopup() {
  const navigate = useNavigate();
  const [show, setShow] = useState(FORCE_SHOW_RENEWAL_POPUP);
  const [subscriptionInRenewal, setSubscriptionInRenewal] = useState(null);

  useEffect(() => {
    if (FORCE_SHOW_RENEWAL_POPUP) {
      setShow(true);
      return;
    }

    if (!isCorporateUser()) return;
    if (wasPopupShownToday()) return;

    const check = async () => {
      try {
        const res = await getActiveSubscriptions();
        const subs = res?.subscriptions || [];
        const inRenewal = subs.find((s) => isWithinRenewalPeriod(s) || isExpired(s));
        if (inRenewal) {
          setSubscriptionInRenewal(inRenewal);
          setShow(true);
        }
      } catch (e) {}
    };

    check();
  }, []);

  const handleClose = () => {
    setShow(false);
    if (!FORCE_SHOW_RENEWAL_POPUP) {
      markPopupShownToday();
    }
  };

  const handleRenew = () => {
    setShow(false);
    if (!FORCE_SHOW_RENEWAL_POPUP) {
      markPopupShownToday();
    }
    navigate(`/subscription/renew?subscriptionId=${subscriptionInRenewal?.id || ''}`);
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="renewal-popup-modal">
      <Modal.Header closeButton>
        <Modal.Title>Subscription Renewal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">
          <RenewalAlertMessage subscription={subscriptionInRenewal} />
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-danger"
          className="renewal-popup-btn-cancel"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button variant="danger" className="renewal-popup-btn-renew" onClick={handleRenew}>
          Renew
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
